import { useCallback, useMemo, useState } from 'react'
import { useWatch } from 'react-hook-form'

import EditIcon from '@mui/icons-material/Edit'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import { useChannelLogisticsQuery } from '~/products/hooks/useChannelLogisticsQuery'
import { type ProductFormSchema, useProductFormContext } from '~/products/hooks/useProductForm'
import { LogisticsProviderDialog } from '../logistics/logistics-provider-dialog'
import { LogisticsTable } from '../logistics/logistics-table'
import { TikTokDeliveryOptions } from './tiktok-attributes'

type ChannelProduct = ProductFormSchema['channel_products'][0]

type TikTokEnabledLogisticsCellProps = {
    shopId: number
    channelId: number
}

function TikTokEnabledLogisticsCell({ shopId, channelId }: TikTokEnabledLogisticsCellProps) {
    const { data, status } = useChannelLogisticsQuery({ channel: 'tiktok', shopId })
    const { control } = useProductFormContext()
    const logisticAttribute = useWatch({
        control,
        name: `channel_attribute_values.${channelId}.product.logistics.value.shop-${shopId}`,
        defaultValue: {
            channel: 'tiktok',
            value: [],
        },
    })

    if (status === 'loading') {
        return <Skeleton width={200} />
    }

    if (status === 'error') {
        return (
            <Typography color="error" variant="subtitle2">
                {gettext('Failed to load delivery options')}
            </Typography>
        )
    }

    if (logisticAttribute.channel !== 'tiktok') {
        return null
    }

    const existingIdSet = new Set(logisticAttribute.value)
    const optionsToDisplay = data.filter((lp) => existingIdSet.has(lp.id)) ?? []

    return (
        <Typography variant="body2">
            {optionsToDisplay.length
                ? optionsToDisplay.map((lp) => lp.description).join(', ')
                : gettext(
                      'No custom delivery options enabled. Use default delivery option of current TikTok shop.'
                  )}
        </Typography>
    )
}

type Props = {
    name: `channel_attribute_values.${string}.product.${string}.value`
    tiktokProducts: ChannelProduct[]
    shopIds: number[]
}

export default function TikTokLogisticOptions({ tiktokProducts }: Props) {
    const [channelProductProvidersOpen, setChannelProductProvidersOpen] = useState(false)
    const [editingProvidersForChannelProductId, setEditingProvidersForChannelProductId] = useState<
        string | number
    >(-1)

    const editingProvidersForChannelProduct = useMemo(() => {
        return tiktokProducts.find((cp) => cp.id === editingProvidersForChannelProductId) ?? null
    }, [editingProvidersForChannelProductId, tiktokProducts])

    const handleEditChannelProductProviders = useCallback((channelProductId: string | number) => {
        setChannelProductProvidersOpen(true)
        setEditingProvidersForChannelProductId(channelProductId)
    }, [])

    return (
        <>
            <LogisticsTable
                channelProducts={tiktokProducts}
                logisticsCol={{
                    cell: ({ row }) => (
                        <TikTokEnabledLogisticsCell
                            shopId={row.original.shop.id}
                            channelId={row.original.shop.channel.id}
                        />
                    ),
                }}
                actionsCol={{
                    cell: ({ row }) => (
                        <Tooltip title={gettext('Edit delivery options')}>
                            <IconButton
                                color="secondary"
                                onClick={() => handleEditChannelProductProviders(row.original.id)}
                            >
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                    ),
                }}
            />

            {editingProvidersForChannelProduct && (
                <LogisticsProviderDialog
                    open={channelProductProvidersOpen}
                    title={editingProvidersForChannelProduct.shop.shop_name}
                    onClose={() => setChannelProductProvidersOpen(false)}
                    onExited={() => setEditingProvidersForChannelProductId(-1)}
                >
                    <TikTokDeliveryOptions shopId={editingProvidersForChannelProduct.shop.id} />
                </LogisticsProviderDialog>
            )}
        </>
    )
}
